/**
 * OptimizationEngine.js
 * Post-processing steps to optimize CPU usage and voice count without sacrificing musical intent.
 */

import { ABSTRACT_ROLES } from '../RoleAllocator.js';
import { ArchetypeRegistry } from '../Analysis/ArchetypeRegistry.js';

// Priority Hierarchy for Unison Culling (Lower index = Higher Priority)
const ROLE_PRIORITY = [
    ABSTRACT_ROLES.MELODY,
    ABSTRACT_ROLES.BASS,
    ABSTRACT_ROLES.COUNTER_MELODY,
    ABSTRACT_ROLES.HARMONY_RHYTHMIC,
    ABSTRACT_ROLES.HARMONY_PAD,
    ABSTRACT_ROLES.HARMONY_COLOR,
    ABSTRACT_ROLES.HARMONY,
    ABSTRACT_ROLES.PERC_LOW,
    ABSTRACT_ROLES.PERC_MID,
    ABSTRACT_ROLES.PERC_HIGH
];

/**
 * Phase 1: The "Compensatory Logic" Architecture
 * Defines relationships between generation variables.
 */
const COMPENSATION_MATRIX = [
    // 1. Cinematic Density & Energy Fix
    // If Density or Energy is Low in Cinematic, we lose the "Wall of Sound".
    // Compensation: Boost Dynamics (make it louder/epic), Spread (wider range), and Motion.
    {
        id: 'cinematic_density_energy_compensator',
        condition: (profile, archetype) => {
            const genre = (profile.harmonySource || '').toLowerCase();
            const isCinematic = genre.includes('cinematic') || genre.includes('epic');
            if (!isCinematic) return false;

            // Trigger if any key variable is critically low
            return (profile.rhythmicDensity < 40) || (profile.energy < 50);
        },
        action: (profile) => {
            console.log('[Optimization] Cinematic Weak Signal detected. Engaging Compensation: Aggressive Boost.');
            // Force High Energy
            profile.energy = Math.max(80, profile.energy);
            // Force Minimum Density
            profile.rhythmicDensity = Math.max(60, profile.rhythmicDensity);
            // Boost Range (Harmonic Spread)
            profile.melodicRange = Math.max(80, profile.melodicRange);
            // Boost Motion to ensure swells are effective
            profile.timbralMotion = Math.max(70, profile.timbralMotion);
        }
    },
    // 1B. Cinematic Range/Motion Fix (Anti-Boxy)
    {
        id: 'cinematic_spatial_compensator',
        condition: (profile, archetype) => {
            const genre = (profile.harmonySource || '').toLowerCase();
            const isCinematic = genre.includes('cinematic') || genre.includes('epic');
            if (!isCinematic) return false;

            return (profile.melodicRange < 40) || (profile.timbralMotion < 40);
        },
        action: (profile) => {
            console.log('[Optimization] Cinematic Spatial Constriction detected. Engaging Compensation: Expanding Field.');
            profile.melodicRange = Math.max(80, profile.melodicRange);
            profile.timbralMotion = Math.max(80, profile.timbralMotion);
        }
    },
    // 2. Jazz Complexity Fix
    // If Complexity is Low in Jazz, we lose the "Color".
    // Compensation: Boost Harmonic Freedom (Extensions) slightly, but focus on Syncopation (Rhythm).
    // 2. Romantic Dynamic Range Fix
    // Romantic music requires high dynamic variance (Swells/Rubato).
    // If Timbral Motion (Dynamics) is low, score fails.
    {
        id: 'romantic_dynamics_compensator',
        condition: (profile, archetype) => {
            const genre = (profile.harmonySource || '').toLowerCase();
            return (genre.includes('romantic') || genre.includes('piano'))
                   && profile.timbralMotion < 60; // Needs significant motion
        },
        action: (profile) => {
            console.log('[Optimization] Romantic Flat Dynamics detected. Engaging Compensation: Boost Motion & Range.');
            // Force High Motion for Swells
            profile.timbralMotion = Math.max(85, profile.timbralMotion);
            // Ensure Range is wide for sweeping melodies
            profile.melodicRange = Math.max(70, profile.melodicRange);
            // Ensure Energy isn't too low (or too high, but usually low is the problem for variance)
            profile.energy = Math.max(60, profile.energy);
        }
    },
    {
        id: 'jazz_complexity_compensator',
        condition: (profile, archetype) => {
            const genre = (profile.harmonySource || '').toLowerCase();
            return (genre.includes('jazz') || genre.includes('fusion'))
                   && profile.harmonicComplexity < 30;
        },
        action: (profile) => {
            console.log('[Optimization] Jazz Low Complexity detected. Engaging Compensation: Boost Syncopation (via Motion).');
            // Boost Harmonic Freedom to a minimum floor (ensure 7ths)
            profile.harmonicComplexity = Math.max(40, profile.harmonicComplexity);
            // Boost Timbral Motion to ensure rhythmic interest
            profile.timbralMotion = Math.min(100, (profile.timbralMotion || 50) + 25);
        }
    },
    // 3. Baroque Structure Fix
    // Baroque requires steady motor rhythm and stable dynamics (Terraced).
    // High Motion or Low Density breaks the archetype.
    {
        id: 'baroque_structure_compensator',
        condition: (profile, archetype) => {
            const genre = (profile.harmonySource || '').toLowerCase();
            return (genre.includes('baroque') || genre.includes('classical'))
                   && (profile.timbralMotion > 40 || profile.rhythmicDensity < 50);
        },
        action: (profile) => {
            console.log('[Optimization] Baroque Instability detected. Engaging Compensation: Stabilize Motion & Boost Density.');
            // Force Low Motion (Terraced Dynamics)
            profile.timbralMotion = Math.min(30, profile.timbralMotion);
            // Ensure High Density (Motor Rhythm)
            profile.rhythmicDensity = Math.max(70, profile.rhythmicDensity);
            // Ensure Triadic Harmony
            profile.harmonicComplexity = Math.min(40, profile.harmonicComplexity);
        }
    },
    // 3B. Fantasy Sparkle Fix
    {
        id: 'fantasy_sparkle_compensator',
        condition: (profile, archetype) => {
            const genre = (profile.harmonySource || '').toLowerCase();
            return genre.includes('fantasy') && (profile.timbralMotion < 50 || profile.melodicRange < 50);
        },
        action: (profile) => {
            console.log('[Optimization] Fantasy Lack of Sparkle detected. Engaging Compensation: Boost Motion & Range.');
            profile.timbralMotion = Math.max(70, profile.timbralMotion);
            profile.melodicRange = Math.max(75, profile.melodicRange);
        }
    },
    // 4. High Density Minimalism Fix
    // If Density is High in Minimalist, it breaks the genre.
    // Compensation: Cap Density, Boost Repetition (handled by Archetype logic usually, but here we can enforce).
    {
        id: 'minimalist_density_cap',
        condition: (profile, archetype) => {
            const genre = (profile.harmonySource || '').toLowerCase();
            return (genre.includes('minimalist') || genre.includes('pulse'))
                   && profile.rhythmicDensity > 60;
        },
        action: (profile) => {
            console.log('[Optimization] Minimalist High Density detected. Engaging Compensation: Clamping Density.');
            profile.rhythmicDensity = 60; // Soft clamp
            // Reduce Range to keep it focused
            profile.melodicRange = Math.min(profile.melodicRange, 40);
        }
    },
    // 4. Rock/Metal Intensity Fix
    // If Energy is Low in Rock, it sounds weak.
    // Compensation: Boost Velocity/Energy.
    {
        id: 'rock_energy_boost',
        condition: (profile, archetype) => {
            const genre = (profile.harmonySource || '').toLowerCase();
            return (genre.includes('rock') || genre.includes('metal'))
                   && profile.energy < 40;
        },
        action: (profile) => {
            console.log('[Optimization] Rock Low Energy detected. Engaging Compensation: Boost Energy.');
            profile.energy = Math.max(60, profile.energy + 20);
        }
    },
    // 5. Action Density & Energy Fix
    {
        id: 'action_intensity_compensator',
        condition: (profile, archetype) => {
            const genre = (profile.harmonySource || '').toLowerCase();
            const isAction = genre.includes('action') || genre.includes('chase');
            if (!isAction) return false;

            return (profile.rhythmicDensity < 50) || (profile.energy < 60);
        },
        action: (profile) => {
            console.log('[Optimization] Action Low Intensity detected. Engaging Compensation: Boost Density & Energy.');
            profile.rhythmicDensity = Math.max(80, profile.rhythmicDensity);
            profile.energy = Math.max(90, profile.energy);
            // Boost Motion for dramatic hits
            profile.timbralMotion = Math.max(70, profile.timbralMotion);
        }
    }
];

/**
 * Task 2A: The Cost Function
 * Calculates a "Risk Score" based on deviation from Archetype.
 * @param {Object} profile - User input profile (0-100 sliders).
 * @param {Object} archetype - Genre Archetype.
 * @returns {Object} Risk Report.
 */
export const calculateParameterRisk = (profile, archetype) => {
    // Determine Genre Category for heuristic check
    // In a full implementation, this would compare against Archetype.parameterRanges
    const genre = (profile.harmonySource || '').toLowerCase();
    let risk = 0;
    const details = [];

    const density = profile.rhythmicDensity || 50;
    const complexity = profile.harmonicComplexity || 50;
    const energy = profile.energy || 50;

    if (genre.includes('cinematic')) {
        if (density < 30) { risk += 0.4; details.push('Density < 30'); }
        if (energy < 40) { risk += 0.3; details.push('Energy < 40'); }
    } else if (genre.includes('jazz')) {
        if (complexity < 30) { risk += 0.4; details.push('Complexity < 30'); }
    } else if (genre.includes('minimalist')) {
        if (density > 60) { risk += 0.5; details.push('Density > 60'); }
    }

    return { risk, details };
};

/**
 * Task 2B: Dynamic Variable Injection (Optimization Engine)
 * Modifies the User Profile IN-PLACE to ensure Genre Fidelity.
 * @param {Object} profile - User input profile.
 * @returns {Object} The optimized profile (same reference).
 */
export const optimizeProfile = (profile) => {
    const archetype = ArchetypeRegistry.get(profile.harmonySource);

    // Calculate initial risk (Observability)
    const initialRisk = calculateParameterRisk(profile, archetype);
    if (initialRisk.risk > 0) {
        console.log(`[Optimization] Profile Risk: ${(initialRisk.risk * 100).toFixed(0)}%`, initialRisk.details);
    }

    // Run Compensation Matrix
    COMPENSATION_MATRIX.forEach(rule => {
        if (rule.condition(profile, archetype)) {
            rule.action(profile);
        }
    });

    return profile;
};

/**
 * Culls redundant unison notes to save CPU.
 * If two instruments play the same pitch at the same time, the lower priority one is dropped.
 * @param {Array} events - The full list of generated events.
 * @returns {Array} - Optimized event list.
 */
export const cullUnisonNotes = (events) => {
    if (!events || events.length === 0) return [];

    // Group by quantized time + pitch
    // Key: "time_pitch" -> [event1, event2...]
    const collisions = new Map();

    events.forEach(ev => {
        // Quantize time to avoid floating point mismatches (e.g. 1ms tolerance)
        const qTime = Math.round(ev.s * 100) / 100;
        const pitch = ev.d;

        // Skip percussion or non-pitched events if necessary?
        // Percussion usually doesn't have 'pitch' in the same sense (MIDI note 36 is Kick).
        // But 2 kicks at same time is also redundant? Yes.

        const key = `${qTime}_${pitch}`;

        if (!collisions.has(key)) {
            collisions.set(key, []);
        }
        collisions.get(key).push(ev);
    });

    const keptEvents = [];

    collisions.forEach((group) => {
        if (group.length === 1) {
            keptEvents.push(group[0]);
        } else {
            // Collision detected!
            // Sort by Priority
            group.sort((a, b) => {
                const pA = ROLE_PRIORITY.indexOf(a.role);
                const pB = ROLE_PRIORITY.indexOf(b.role);
                // If roles are same, keep first? Or maybe sort by velocity?
                // If priority index is -1 (unknown), treat as lowest priority (Infinity)
                const rankA = pA === -1 ? 999 : pA;
                const rankB = pB === -1 ? 999 : pB;

                if (rankA !== rankB) return rankA - rankB; // Lower index wins

                // Tie-breaker: Velocity (Louder wins)
                return (b.vel || 0) - (a.vel || 0);
            });

            // Keep the winner
            keptEvents.push(group[0]);

            // Log/Debug (Optional)
            // console.log(`Culled ${group.length - 1} voices at ${group[0].s} (Pitch ${group[0].d})`);
        }
    });

    // Re-sort by time
    return keptEvents.sort((a, b) => a.s - b.s);
};

/**
 * Task 3: "Voice Stealing" Safety Valve
 * Caps active polyphony per track to prevent CPU spikes with overlapping clouds.
 * Constraint Fix: Dynamic Polyphony Cap (Configurable, defaults higher).
 * @param {Array} events - All events.
 * @param {number} maxPolyphony - Max concurrent voices per instrument track.
 */
export const capPolyphony = (events, maxPolyphony = 12) => {
    if (!events || events.length === 0) return [];

    // Group by Instrument Track
    const byTrack = {};
    events.forEach(ev => {
        const key = ev.instrumentId || ev.role || 'global';
        if (!byTrack[key]) byTrack[key] = [];
        byTrack[key].push(ev);
    });

    const result = [];

    Object.values(byTrack).forEach(trackEvents => {
        trackEvents.sort((a, b) => a.s - b.s);

        // Active Voices: [ { event, endTime } ]
        let activeVoices = [];
        const processed = [];

        trackEvents.forEach(ev => {
            // Prune expired voices
            activeVoices = activeVoices.filter(v => v.endTime > ev.s);

            if (activeVoices.length >= maxPolyphony) {
                // Steal!
                // Strategy: Kill the oldest voice (closest to start time)?
                // Or closest to end time?
                // Standard synth logic: Kill oldest (first in list usually if sorted by trigger).
                // Sort by start time (oldest first)
                activeVoices.sort((a, b) => a.event.s - b.event.s);

                const victim = activeVoices.shift();

                // Truncate victim in the output list?
                // We need reference to the object in 'processed' array.
                // Mutating victim.event.len
                const newLen = Math.max(0.05, ev.s - victim.event.s);
                victim.event.len = newLen;

                // Victim removed from active set, but stays in processed list with new len.
            }

            activeVoices.push({ event: ev, endTime: ev.s + ev.len });
            processed.push(ev);
        });

        result.push(...processed);
    });

    return result.sort((a, b) => a.s - b.s);
};
